import { toast } from 'react-hot-toast';
import { supabase } from '../supabase';
import { miners as fallbackMiners } from '../miners';
import { updateMinersCache } from '../miners';
import type { MinerSpec } from '../minerTypes';

const MINERSTAT_API_URL = 'https://api.minerstat.com/v2';

interface MinerstatHardware {
  id: string;
  name: string;
  type: string;
  algorithm: string;
  power: number;
  hashrate: {
    hash: number;
    unit: string;
  };
  release: string;
  manufacturer: string;
  specs: {
    'Release date'?: string;
    'Size'?: string;
    'Weight'?: string;
    'Noise level'?: string;
    'Number of chips'?: string;
    'Chip type'?: string;
    [key: string]: string | undefined;
  };
}

export async function fetchMinerstatHardware(): Promise<MinerstatHardware[]> {
  try {
    console.log('Fetching data from Minerstat API...');
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

    const response = await fetch(`${MINERSTAT_API_URL}/hardware?type=asic`, {
      signal: controller.signal,
      headers: {
        'Accept': 'application/json',
        'Cache-Control': 'no-cache'
      }
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`API responded with status: ${response.status}`);
    }

    const data = await response.json();
    
    if (!Array.isArray(data)) {
      throw new Error('Invalid data format received');
    }

    console.log(`Fetched ${data.length} miners from Minerstat`);
    return data;
  } catch (error: any) {
    console.warn('Error fetching Minerstat hardware:', error);
    if (error.name === 'AbortError') {
      toast.error('Request timed out - using local data');
    }
    return [];
  }
}

export function convertMinerstatToMinerSpec(minerstatData: MinerstatHardware): MinerSpec | null {
  try {
    // Normalize algorithm
    const normalizedAlgorithm = (() => {
      const algo = minerstatData.algorithm.toLowerCase();
      if (algo.includes('sha-256') || algo === 'sha256') return 'SHA-256';
      if (algo === 'scrypt') return 'Scrypt';
      if (algo === 'ethash') return 'Ethash';
      if (algo === 'blake2s') return 'Blake2s';
      return null;
    })();

    if (!normalizedAlgorithm) {
      console.warn('Unsupported algorithm:', minerstatData.algorithm);
      return null;
    }

    // Parse release year
    const releaseYear = parseInt(minerstatData.release.split('-')[0]);
    if (isNaN(releaseYear) || releaseYear < 2009 || releaseYear > new Date().getFullYear() + 1) {
      console.warn('Invalid release year:', minerstatData.release);
      return null;
    }

    // Normalize hashrate unit
    const normalizedUnit = (() => {
      const unit = minerstatData.hashrate.unit.toUpperCase();
      if (['H/S', 'KH/S', 'MH/S', 'GH/S', 'TH/S', 'PH/S', 'EH/S'].includes(unit)) {
        return unit.charAt(0) + unit.slice(1).toLowerCase();
      }
      return null;
    })();

    if (!normalizedUnit) {
      console.warn('Invalid hashrate unit:', minerstatData.hashrate.unit);
      return null;
    }

    // Determine coin based on algorithm
    const coin = {
      'SHA-256': 'BTC',
      'Scrypt': 'LTC',
      'Ethash': 'ETH',
      'Blake2s': 'KDA'
    }[normalizedAlgorithm] || 'BTC';

    return {
      name: minerstatData.name,
      model: minerstatData.id,
      algorithm: normalizedAlgorithm,
      hashrate: minerstatData.hashrate.hash,
      hashrateUnit: normalizedUnit,
      power: minerstatData.power,
      coin,
      manufacturer: minerstatData.manufacturer,
      releaseYear,
      status: 'current',
      efficiency: minerstatData.hashrate.hash / minerstatData.power,
      noise: parseInt(String(minerstatData.specs['Noise level'] || '').replace(/[^0-9]/g, '') || '75'),
      cooling: 'Air',
      interface: 'Ethernet',
      temperature: {
        min: 5,
        max: 45
      },
      humidity: {
        min: 5,
        max: 95
      },
      supportedCoins: [coin]
    };
  } catch (error) {
    console.error('Error converting Minerstat data:', error);
    return null;
  }
}

export async function importMinerstatToSupabase(): Promise<{ 
  inserted: number; 
  updated: number; 
  skipped: number; 
}> {
  try {
    console.log('Starting Minerstat import...');
    const minerstatData = await fetchMinerstatHardware();
    
    if (!minerstatData.length) {
      throw new Error('No data received from Minerstat');
    }

    console.log(`Fetched ${minerstatData.length} miners from Minerstat`);

    // Convert data to format expected by Supabase function
    const formattedData = minerstatData
      .filter(miner => miner.type === 'asic')
      .map(miner => ({
        model: miner.id,
        name: miner.name,
        algorithm: miner.algorithm,
        hashrate: miner.hashrate.hash,
        hashrate_unit: miner.hashrate.unit,
        power: miner.power,
        manufacturer: miner.manufacturer,
        release_year: parseInt(miner.release.split('-')[0]),
        specs: {
          noise: parseInt(String(miner.specs['Noise level'] || '').replace(/[^0-9]/g, '') || '75'),
          cooling: 'Air',
          interface: 'Ethernet',
          dimensions: miner.specs['Size'],
          weight: parseFloat(String(miner.specs['Weight'] || '').replace(/[^0-9.]/g, '') || '15'),
          chip_type: miner.specs['Chip type'],
          chip_count: parseInt(String(miner.specs['Number of chips'] || '').replace(/[^0-9]/g, '') || '0')
        }
      }));

    console.log(`Formatted ${formattedData.length} miners for import`);

    // Import data using the database function
    const { data: result, error } = await supabase
      .rpc('import_minerstat_data', {
        minerstat_data: formattedData
      });

    if (error) throw error;

    if (!result) {
      throw new Error('No result returned from import function');
    }

    // Update local cache with new data
    const { data: updatedMiners } = await supabase
      .from('miners')
      .select('*')
      .order('release_year', { ascending: false });

    if (updatedMiners) {
      updateMinersCache(updatedMiners);
    }

    // Return the results
    return result;
  } catch (error: any) {
    console.error('Error importing Minerstat data:', error);
    toast.error('Failed to import Minerstat data');
    throw error;
  }
}

// Export fallback data for use when API fails
export const getFallbackMiners = () => fallbackMiners;